import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Badge, Card, Empty, Spinner, useToast } from '../components/shared/UI';
import RecurringModal from '../components/documents/RecurringModal';
import { fmtCurrency, fmtDate } from '../utils/helpers';
import { useT } from '../i18n';
import api from '../utils/api';

const STATUS_COLORS = { active: '#10B981', paused: '#F59E0B', ended: '#94a3b8' };

export default function Recurring() {
  const navigate = useNavigate();
  const { t } = useT();
  const { show, ToastContainer } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => api.get('/recurring').then(r => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setStatus = async (s, status) => {
    setBusy(s._id);
    try { await api.put(`/recurring/${s._id}`, { status }); await load(); show(t('recurring.saved')); }
    catch (err) { show(err.response?.data?.message || t('recurring.failed'), 'error'); }
    finally { setBusy(''); }
  };

  const runNow = async s => {
    if (!window.confirm(t('recurring.runConfirm'))) return;
    setBusy(s._id);
    try {
      const { data } = await api.post(`/recurring/${s._id}/run`);
      show(t('recurring.ran', { n: data.docNumber }));
      navigate(`/documents/${data._id}`);
    } catch (err) { show(err.response?.data?.message || t('recurring.failed'), 'error'); setBusy(''); }
  };

  const del = async s => {
    if (!window.confirm(t('recurring.deleteConfirm'))) return;
    await api.delete(`/recurring/${s._id}`);
    setList(l => l.filter(x => x._id !== s._id));
    show(t('recurring.deleted'));
  };

  return (
    <div>
      <ToastContainer />
      <PageHeader title={t('recurring.title')} subtitle={t('recurring.subtitle')} />

      {loading ? <Spinner /> : list.length === 0
        ? <Empty icon="🔁" message={t('recurring.empty')} action={<Btn variant="primary" onClick={() => navigate('/invoices')}>{t('recurring.goInvoices')}</Btn>} />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>{t('recurring.thClient')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('recurring.thAmount')}</th>
                    <th style={th}>{t('recurring.thSchedule')}</th>
                    <th style={th}>{t('recurring.thNext')}</th>
                    <th style={th}>{t('recurring.thStatus')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('docs.thActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((s, i) => (
                    <tr key={s._id} style={{ borderBottom: '1px solid var(--border-light)', background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{s.label || s.client?.name || '—'}</div>
                        <div style={sub}>
                          {s.sourceDoc && t('recurring.basedOn', { n: s.sourceDoc.docNumber })}
                          {s.runCount > 0 && ` · ${t('recurring.issued', { n: s.runCount })}`}
                        </div>
                        {s.lastDocument && (
                          <button onClick={() => navigate(`/documents/${s.lastDocument._id}`)} style={linkBtn}>{t('recurring.last', { n: s.lastDocument.docNumber })}</button>
                        )}
                        {s.lastError && <div style={{ ...sub, color: 'var(--danger)' }}>⚠ {t('recurring.lastError', { msg: s.lastError })}</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtCurrency(s.template?.total, s.template?.currencySymbol)}</td>
                      <td style={td}>
                        <div>{t('recurring.summary', { n: s.interval, unit: t(`recurring.f_${s.frequency}`) })}</div>
                        <div style={sub}>
                          {s.autoSend ? `✉ ${t('recurring.autoSendOn')}` : t('recurring.asDraft')}
                          {s.endDate && ` · ${t('recurring.until', { date: fmtDate(s.endDate) })}`}
                        </div>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.status === 'active' && s.nextRunAt ? fmtDate(s.nextRunAt) : '—'}</td>
                      <td style={td}><Badge label={t(`recurring.s_${s.status}`)} color={STATUS_COLORS[s.status]} /></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Btn size="sm" variant="ghost" disabled={busy === s._id} onClick={() => runNow(s)}>{t('recurring.runNow')}</Btn>
                          {s.status === 'active'
                            ? <Btn size="sm" variant="ghost" disabled={busy === s._id} onClick={() => setStatus(s, 'paused')}>{t('recurring.pause')}</Btn>
                            : <Btn size="sm" variant="ghost" disabled={busy === s._id} onClick={() => setStatus(s, 'active')}>{t('recurring.resume')}</Btn>}
                          <Btn size="sm" variant="ghost" onClick={() => setEditing(s)}>{t('common.edit')}</Btn>
                          <Btn size="sm" variant="danger" onClick={() => del(s)}>{t('docs.del')}</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      <RecurringModal open={!!editing} onClose={() => setEditing(null)} schedule={editing} onSaved={load} show={show} />
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '13px 16px', fontSize: 14, color: 'var(--text)', verticalAlign: 'top' };
const sub = { fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 };
const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 12, fontFamily: 'inherit', padding: 0, marginTop: 2 };
